import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, collection } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const InstitutionDataContext = createContext();

const INSTITUTION_CACHE_TTL = 365 * 24 * 60 * 60 * 1000;
const CACHE_KEY = "institution_data";

// Límite duro de Firestore por documento y umbral de advertencia
const FIRESTORE_DOC_LIMIT_BYTES = 1048576; // 1 MB
const DIRECTORY_WARNING_THRESHOLD = FIRESTORE_DOC_LIMIT_BYTES * 0.8; // 80%

export function InstitutionProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentsIndex, setStudentsIndex] = useState(() => {
    try {
      const cached = localStorage.getItem('directorio_maestro');
      if (cached) {
        const arr = JSON.parse(cached);
        const index = {};
        arr.forEach(s => { if (s.id) index[s.id] = s; });
        return index;
      }
    } catch { }
    return {};
  });
  // El índice está disponible para quien quiera reutilizarlo (la reconciliación
  // con el VPS): se enciende cuando el guardián del directorio ya resolvió.
  const [indiceListo, setIndiceListo] = useState(false);
  const [classesFromIndex, setClassesFromIndex] = useState(() => {
    try {
      const cached = localStorage.getItem('classes_from_index');
      if (cached) return JSON.parse(cached);
    } catch { }
    return [];
  });

  const [personalClasses, setPersonalClasses] = useState(() => {
    try {
      const cached = localStorage.getItem(`personal_classes_list_${user?.uid}`);
      if (cached) return JSON.parse(cached);
    } catch { }
    return [];
  });

  // Estado de tamaño del directorio maestro (documento único, límite 1MB de Firestore)
  const [directorySizeInfo, setDirectorySizeInfo] = useState({
    bytes: 0,
    percent: 0,
    warning: false
  });

  const fetchInstitution = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "institution", "datos"), navigator.onLine ? { source: 'server' } : undefined);
      if (snap.exists()) {
        const data = snap.data();
        const lastUpdateSeconds = data.lastUpdate?.seconds || null;

        if (user && user.cachedAt && lastUpdateSeconds) {
          if (lastUpdateSeconds > (user.cachedAt / 1000)) {
            refreshUser();
          }
        }

        const toStore = { ...data, __cachedAt: Date.now(), __lastUpdateSeconds: lastUpdateSeconds };
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
        setInstitution(toStore);
      }
    } catch {
      // si falla, mantenemos institution como null
    } finally {
      setLoading(false);
    }
  }, [user, refreshUser]);

  // === Guardián del Directorio Maestro (Índice Global de Estudiantes) ===
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(doc(db, 'directorio_institucional', 'metadata'), async (metaSnap) => {
      if (metaSnap.exists()) {
        const serverVersion = metaSnap.data().lastUpdate || 0;
        const cachedVersion = localStorage.getItem('directorio_maestro_version');
        const localVersion = cachedVersion ? parseInt(cachedVersion, 10) : 0;
        const hasCachedData = localStorage.getItem('directorio_maestro') !== null;

        if (serverVersion > localVersion || !hasCachedData) {
          try {
            console.log("[Índice] Nueva versión detectada. Descargando...");
            const docSnap = await getDoc(doc(db, 'directorio_institucional', 'estudiantes'));
            if (docSnap.exists()) {
              const raw = docSnap.data();

              // Medir tamaño aproximado del documento (0 lecturas extra: reutiliza `raw`
              // ya descargado). Advierte si se acerca al límite de 1MB de Firestore.
              try {
                const sizeBytes = new Blob([JSON.stringify(raw)]).size;
                const percent = (sizeBytes / FIRESTORE_DOC_LIMIT_BYTES) * 100;
                const warning = sizeBytes > DIRECTORY_WARNING_THRESHOLD;
                setDirectorySizeInfo({ bytes: sizeBytes, percent, warning });
                if (warning) {
                  console.warn(
                    `[Directorio] Documento "estudiantes" al ${percent.toFixed(1)}% de su capacidad ` +
                    `(${sizeBytes} / ${FIRESTORE_DOC_LIMIT_BYTES} bytes). Se recomienda no superar ` +
                    `700-800 estudiantes activos por institución hasta la limpieza de fin de año.`
                  );
                }
              } catch (sizeErr) {
                console.error("[Directorio] Error midiendo tamaño:", sizeErr);
              }

              // Expandir claves minificadas a nombres descriptivos
              const students = {};
              Object.keys(raw).forEach(key => {
                if (key.startsWith('_')) return;
                const s = raw[key];
                // Un valor nulo o escalar rompía `s.id` y, al no haber
                // ErrorBoundary en el render, dejaba la app en blanco.
                if (!s || typeof s !== 'object' || Array.isArray(s)) return;
                students[key] = {
                  id: s.id,
                  nie: s.nie,
                  apellidos: s.ap || '',
                  nombres: s.no || '',
                  claseActual: s.cA || '',
                  estado: s.s || 'activo',
                  email: s.em || '',
                  whatsapp: s.wa || '',
                  responsableId: s.rID || '',
                  inactivadoEn: s.iE || null,
                  motivoInactivacion: s.mI || '',
                  movedTo: s.mt || '',
                  movedToSection: s.mts || '',
                  transferHistory: s.tH || []
                };
              });
              const arr = Object.values(students);

              localStorage.setItem('directorio_maestro', JSON.stringify(arr));
              localStorage.setItem('directorio_maestro_version', serverVersion.toString());
              setStudentsIndex(students);

              window.dispatchEvent(new Event('directorio_maestro_updated'));
              console.log("[Índice] Actualizado:", arr.length, "estudiantes");
            } else {
              localStorage.removeItem('directorio_maestro');
              localStorage.removeItem('directorio_maestro_version');
              setStudentsIndex({});
              setDirectorySizeInfo({ bytes: 0, percent: 0, warning: false });
              window.dispatchEvent(new Event('directorio_maestro_updated'));
            }
          } catch (err) {
            console.error("[Índice] Error sincronizando:", err);
          }
        }
      }
      // Ya sea que haya descargado o que el de localStorage sirviera, el directorio
      // está en memoria: la reconciliación puede usarlo sin leerlo otra vez.
      setIndiceListo(true);
    });

    return () => unsub();
  }, [user]);

  // === Guardián de Clases Institucionales (onSnapshot directo, 0 lecturas extra) ===
  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(doc(db, 'directorio_institucional', 'clases'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const classArray = Object.entries(data)
          .filter(([key, info]) => key !== 'lastUpdate' && key !== 'metadata' && info && typeof info === 'object')
          .map(([id, info]) => ({
            id,
            displayId: info.displayId || '',
            section: info.section || '',
            name: info.name || '',
            teacherName: info.teacherName || info.encargado || '',
            activeStudentsCount: info.activeStudentsCount ?? info.activeCount ?? 0,
            version: 0
          }));
        setClassesFromIndex(classArray);
        localStorage.setItem('classes_from_index', JSON.stringify(classArray));
        // El directorio es la única fuente de la lista de clases: de acá se
        // alimenta el caché que leen Inicio, Horario y Reportes.
        localStorage.setItem('clases_institucionales_list', JSON.stringify(classArray));
      } else {
        setClassesFromIndex([]);
        localStorage.removeItem('classes_from_index');
      }
    });

    return () => unsub();
  }, [user]);

  // === Datos derivados del índice ===
  const studentsByClass = useMemo(() => {
    const map = {};
    Object.values(studentsIndex).forEach(s => {
      // Una identidad canónica; proyectar una sola tarjeta histórica por
      // clase anterior, incluso en recorridos A→B→A.
      const estado = s.estado || s.status || 'activo';
      if (s.claseActual) {
        if (!map[s.claseActual]) map[s.claseActual] = [];
        map[s.claseActual].push({ ...s, estado, disabled: estado !== 'activo' });
      }
      const previousClasses = new Set((s.transferHistory || []).map(p => p.classId || p.id).filter(Boolean));
      previousClasses.delete(s.claseActual);
      for (const previous of previousClasses) {
        if (!map[previous]) map[previous] = [];
        map[previous].push({ ...s, estado: 'trasladado', disabled: true, motivoInactivacion: 'trasladado' });
      }
    });
    return map;
  }, [studentsIndex]);

  const studentsByNie = useMemo(() => studentsIndex, [studentsIndex]);

  const nieToId = useMemo(() => {
    const map = {};
    Object.values(studentsIndex).forEach(s => {
      if (s.nie) map[s.nie] = s.id;
    });
    return map;
  }, [studentsIndex]);

  const getStudentsByClass = useCallback((classId) => {
    return studentsByClass[classId] || [];
  }, [studentsByClass]);

  const getStudentByNie = useCallback((nie) => {
    const id = nieToId[nie];
    return id ? studentsIndex[id] : null;
  }, [studentsIndex, nieToId]);

  const getActiveStudentsByClass = useCallback((classId) => {
    return (studentsByClass[classId] || []).filter(s => s.estado === 'activo');
  }, [studentsByClass]);

  const getActiveStudentCount = useCallback((classId) => {
    return (studentsByClass[classId] || []).filter(s => s.estado === 'activo').length;
  }, [studentsByClass]);

  // === Carga de datos institucionales ===
  useEffect(() => {
    const localRaw = window.localStorage.getItem(CACHE_KEY);
    if (localRaw) {
      let parsed = null;
      try {
        parsed = JSON.parse(localRaw);
        setInstitution(parsed);
      } catch {
        setInstitution(null);
      }

      const cachedAt = parsed?.__cachedAt || null;
      const isExpired = !cachedAt || (Date.now() - cachedAt) >= INSTITUTION_CACHE_TTL;

      if (!isExpired) {
        if (user && user.cachedAt && parsed?.__lastUpdateSeconds) {
          if (parsed.__lastUpdateSeconds > (user.cachedAt / 1000)) {
            refreshUser();
          }
        }
        setLoading(false);
        return;
      }

      fetchInstitution();
    } else {
      fetchInstitution();
    }
  }, [user, refreshUser, fetchInstitution]);

  const updateInstitution = useCallback(async (newData) => {
    setLoading(true);
    try {
      await setDoc(doc(db, "institution", "datos"), { ...newData, lastUpdate: serverTimestamp() }, { merge: true });

      try {
        const snap = await getDoc(doc(db, "institution", "datos"));
        if (snap.exists()) {
          const data = snap.data();
          const toStore = { ...data, __cachedAt: Date.now(), __lastUpdateSeconds: data.lastUpdate?.seconds || null };
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
          setInstitution(toStore);
        }
      } catch {
        setInstitution(newData);
      }
    } catch (err) {
      console.error("Error updating institution:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // === Sincronización reactiva de la configuración institucional ===
  // Antes colgaba de `clases_institucionales/metadata.inst_ver`, una señal aparte
  // que había que mantener. Ahora se escucha el documento real: cualquier cambio
  // de configuración o de calendario llega a todas las pestañas.
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, "institution", "datos"), (snap) => {
      if (!snap.exists()) return;

      const data = snap.data();
      const lastUpdateSeconds = data.lastUpdate?.seconds || null;

      if (user.cachedAt && lastUpdateSeconds && lastUpdateSeconds > (user.cachedAt / 1000)) {
        refreshUser();
      }

      const toStore = { ...data, __cachedAt: Date.now(), __lastUpdateSeconds: lastUpdateSeconds };
      localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
      setInstitution(toStore);
    });

    return () => unsubscribe();
  }, [user, refreshUser]);

  useEffect(() => {
    if (!user?.uid) {
      setPersonalClasses([]);
      return;
    }
    const unsub = onSnapshot(collection(db, 'users', user.uid, 'classes_personal'), (snap) => {
      const classes = [];
      snap.forEach(doc => {
        if (doc.id === 'metadata') return;
        const data = doc.data();
        classes.push({
          id: doc.id,
          ...data,
          teacherName: data.teacherName || data.encargado || ''
        });
      });
      setPersonalClasses(classes);
      localStorage.setItem(`personal_classes_list_${user.uid}`, JSON.stringify(classes));
    }, (err) => {
      console.warn("personalClasses onSnapshot error:", err);
    });
    return unsub;
  }, [user?.uid]);

  const clearInstitutionCache = useCallback(() => {
    window.localStorage.removeItem(CACHE_KEY);
    setInstitution(null);
    setLoading(true);
  }, []);

  return (
    <InstitutionDataContext.Provider
      value={{
        institution,
        loading,
        updateInstitution,
        clearInstitutionCache,
        studentsIndex,
        indiceListo,
        studentsByClass,
        studentsByNie,
        classesFromIndex,
        getStudentsByClass,
        getStudentByNie,
        getActiveStudentsByClass,
        getActiveStudentCount,
        personalClasses,
        directorySizeInfo,
      }}
    >
      {children}
    </InstitutionDataContext.Provider>
  );
}

export function useInstitutionData() {
  return useContext(InstitutionDataContext);
}
